/* eslint-disable no-magic-numbers */ // inline style literals (sizes, colors)
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { ScreenReaderOnly } from '@amzn/stencil-react-components/screen-reader-only'
import { Text } from '@amzn/stencil-react-components/text'

import {
    BucketAndCupSelectionStepDTO,
    BucketDTO,
    BucketsAndCupsGroupDTO,
    BucketSelectionRule,
    CupDTO,
    CupSelectionRule,
} from 'src/models/dto/activities/BucketsAndCupsGroupDTO'
import {
    bucketRankColor,
    C,
    colorForItemType,
    rankColor,
} from 'src/pages/module-builder/buckets-and-cups-editor/bucketsAndCupsVisualizerTheme'
import { SelectionPlan } from 'src/services/bucketsAndCups/BucketsAndCupsSelectionPlanService'

/**
 * Optional summary of an item that lives on a page in the ItemPool. The website
 * does not model the ItemPool/pages directly (it is owned by the backend), so a
 * consumer supplies a resolver to surface page contents in the "delivered form".
 */
export interface PageItemSummary {
    type: string
    detail?: string
}

export interface BucketsAndCupsVisualizerProps {
    /** The Buckets & Cups selection group to visualize. */
    group: BucketsAndCupsGroupDTO
    /** Optional resolver from a page reference (cup itemId) to that page's items. */
    resolvePageItems?: (pageLabel: string) => PageItemSummary[]
    /**
     * Optional simulate hook. When provided, the visualizer calls the backend
     * Buckets & Cups simulate API to drive which buckets/cups are drawn and to
     * highlight the selected pages, instead of the local heuristic.
     */
    generateSelectionPlan?: () => Promise<SelectionPlan>
}

/* --------------------------------------------------------------- view model */
interface CupView extends CupDTO {
    drawn: boolean
    order: number | null
    pageVariable?: string
    tags: string[]
    /** Pages of this cup selected in this step's execution (for highlight). */
    selectedItemIds?: Set<string>
}
interface BucketView extends BucketDTO {
    selected: boolean
    cupViews: CupView[]
}
interface RankRow {
    rank?: number
    step: BucketAndCupSelectionStepDTO
    ordered: boolean
    totalBuckets: number
    buckets: BucketView[]
}

/**
 * Builds per-rank rows for the diagram. When a plan is supplied, each step
 * (row) is driven by its own execution (`plan.executions[ri]`) so a step shows
 * only the cup/page it drew — not the union across all steps. Exported for unit
 * testing of this per-step mapping.
 */
export function buildRows(
    group: BucketsAndCupsGroupDTO,
    selection: number[],
    plan?: SelectionPlan
): RankRow[] {
    // Each step (row) maps to its own execution in the plan, so a step shows
    // only the cup/page it drew — not the union across all steps.
    let drawnCounter = 0
    return group.bucketAndCupSelectionStep.map((step, ri) => {
        const all = group.buckets.filter((b) => b.rank === step.bucketRank)
        const ordered = step.cupSelectionRule === CupSelectionRule.HIGHEST_VIABLE_RANK
        const execution = plan?.executions[ri]
        const planBuckets = execution ? new Set(execution.bucketIds) : undefined
        const planCups = execution ? new Set(execution.cupIds) : undefined
        const planPages = execution ? new Set(execution.itemVersionIds) : undefined
        const selIdx = Math.min(selection[ri] ?? -1, Math.max(all.length - 1, 0))
        const buckets: BucketView[] = all.map((b, bi) => {
            const cups = [...b.cups].sort((a, z) => a.rank - z.rank)
            const selected = plan ? !!planBuckets?.has(b.bucketId) : bi === selIdx
            const localPick =
                !plan && selected
                    ? Math.min(step.numberOfCupsToPickFromBucket ?? 1, cups.length)
                    : 0
            let patternIdx = 0
            const cupViews: CupView[] = cups.map((c, ci) => {
                const isDrawn = plan ? !!planCups?.has(c.cupId) : selected && ci < localPick
                const pattern = isDrawn ? step.pagePatterns?.[patternIdx] : undefined
                if (isDrawn) {
                    patternIdx += 1
                }
                let order: number | null = null
                if (isDrawn) {
                    drawnCounter += 1
                    order = drawnCounter
                }
                const selectedItemIds =
                    plan && isDrawn && planPages
                        ? new Set(c.itemIds.filter((id) => planPages.has(id)))
                        : undefined
                return {
                    ...c,
                    drawn: isDrawn,
                    order,
                    pageVariable: pattern?.pageVariable,
                    tags: pattern?.tags.map((t) => t.tag) ?? [],
                    selectedItemIds,
                }
            })
            return { ...b, selected, cupViews }
        })
        return {
            rank: step.bucketRank,
            step,
            ordered,
            totalBuckets: all.length,
            buckets,
        }
    })
}

/* ----------------------------------------------------------------- small UI */
export const Pill = ({ text, color }: { text: string; color: string }) => (
    <span
        style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '1px 8px',
            borderRadius: 999,
            background: `${color}1a`,
            color,
            border: `1px solid ${color}66`,
            whiteSpace: 'nowrap',
        }}
    >
        {text}
    </span>
)

export const CornerTag = ({ text, bg }: { text: string; bg: string }) => (
    <span
        style={{
            position: 'absolute',
            top: -7,
            left: 10,
            background: bg,
            padding: '0 5px',
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: '#94a3b8',
        }}
    >
        {text}
    </span>
)

/** Plain-language explanation of what a selection step does, shown per row. */
export const describeStep = (step: BucketAndCupSelectionStepDTO, totalBuckets: number): string => {
    const rankTxt = step.bucketRank != null ? `rank-${step.bucketRank} ` : ''
    const cups = step.numberOfCupsToPickFromBucket ?? 1
    const pages = step.numberOfItemsToPickFromCup ?? 1
    const cupWord = cups === 1 ? 'cup' : 'cups'
    const pageWord = pages === 1 ? 'page' : 'pages'
    const bucketWord = totalBuckets === 1 ? 'bucket' : 'buckets'

    let bucketPart: string
    switch (step.bucketSelectionRule) {
        case BucketSelectionRule.RANDOM:
            bucketPart =
                totalBuckets === 1
                    ? `Selects the only ${rankTxt}bucket`
                    : `Randomly selects 1 of the ${totalBuckets} ${rankTxt}${bucketWord}`
            break
        case BucketSelectionRule.ROUND_ROBIN:
            bucketPart =
                totalBuckets === 1
                    ? `Uses the only ${rankTxt}bucket`
                    : `Rotates to the next of the ${totalBuckets} ${rankTxt}${bucketWord}`
            break
        case BucketSelectionRule.RESUME:
            bucketPart = `Continues in the ${rankTxt}bucket from the previous step`
            break
        default:
            bucketPart = `Selects a ${rankTxt}bucket`
    }

    const cupOrder =
        step.cupSelectionRule === CupSelectionRule.HIGHEST_VIABLE_RANK
            ? 'in ascending cup-rank order (e.g. Main before Follow-up)'
            : 'at random'

    return `${bucketPart}, then draws ${cups} ${cupWord} ${cupOrder}, ${pages} ${pageWord} per cup.`
}

/* -------------------------------------------------------------------- page */
export function PageCard({
    label,
    items,
    highlighted,
    pageVariable,
    tags,
}: {
    label: string
    items: PageItemSummary[]
    highlighted?: boolean
    pageVariable?: string
    tags?: string[]
}) {
    const showPattern = !!highlighted
    return (
        <View
            style={{
                position: 'relative',
                width: 150,
                minHeight: 96,
                background: highlighted ? '#eff6ff' : '#f1f5f9',
                border: highlighted ? `2px solid ${C.blue}` : '1px solid #e2e8f0',
                borderRadius: 6,
                padding: '12px 7px 8px',
                boxShadow: highlighted
                    ? '0 0 0 2px rgba(37,99,235,.15)'
                    : '0 1px 1px rgba(0,0,0,.04)',
            }}
        >
            <CornerTag
                text={highlighted ? 'page · selected' : 'page'}
                bg={highlighted ? '#eff6ff' : '#f1f5f9'}
            />
            {showPattern && pageVariable && (
                <Text fontSize='T75' color={C.blue} fontWeight='bold'>
                    {pageVariable} points to
                </Text>
            )}
            <Text fontSize='T75' fontWeight='bold'>
                {label}
            </Text>
            <Col gridGap='S100' style={{ marginTop: 5 }}>
                {items.length > 0 ? (
                    items.map((it, k) => {
                        const color = colorForItemType(it.type)
                        const tag = showPattern ? tags?.[k] : undefined
                        return (
                            <View
                                key={k}
                                style={{
                                    width: '100%',
                                    background: `${color}1f`,
                                    border: `1px solid ${color}`,
                                    borderLeft: `4px solid ${color}`,
                                    borderRadius: 4,
                                    padding: '3px 6px',
                                }}
                            >
                                <span style={{ fontSize: 10, fontWeight: 300, color: '#334155' }}>
                                    {tag ? `${tag} → ${it.type}` : it.type}
                                </span>
                            </View>
                        )
                    })
                ) : (
                    <Text fontSize='T75' color='neutral70'>
                        no items
                    </Text>
                )}
            </Col>
        </View>
    )
}

function CupChip({
    cup,
    bucketRank,
    resolvePageItems,
}: {
    cup: CupView
    bucketRank?: number
    resolvePageItems?: (pageLabel: string) => PageItemSummary[]
}) {
    const borderColor = rankColor(bucketRank, cup.rank)
    return (
        <View
            style={{
                position: 'relative',
                flex: '0 0 auto',
                minWidth: 187,
                maxWidth: 506,
                border: cup.drawn ? '3px solid #111827' : `1px solid ${borderColor}`,
                borderRadius: 8,
                padding: '8px 8px 6px',
                background: cup.drawn ? '#fff' : '#fafafa',
            }}
        >
            <CornerTag text='cup' bg={cup.drawn ? '#fff' : '#fafafa'} />
            <Row gridGap='S100' alignItems='center' style={{ justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#111827' }}>
                    {cup.cupId}
                    {cup.drawn && <ScreenReaderOnly> (selected in this step)</ScreenReaderOnly>}
                </span>
                {cup.order != null && (
                    <span
                        aria-label={`delivery order ${cup.order}`}
                        style={{
                            minWidth: 18,
                            height: 18,
                            borderRadius: 9,
                            background: C.blue,
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 4px',
                        }}
                    >
                        {cup.order}
                    </span>
                )}
            </Row>
            <Text fontSize='T75' color='neutral70'>
                rank {cup.rank} · {cup.itemIds.length} page(s)
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {cup.itemIds.map((label) => (
                    <PageCard
                        key={label}
                        label={label}
                        items={resolvePageItems ? resolvePageItems(label) : []}
                        highlighted={cup.selectedItemIds?.has(label)}
                        pageVariable={cup.pageVariable}
                        tags={cup.tags}
                    />
                ))}
            </div>
        </View>
    )
}

/* ------------------------------------------------------------------ bucket */
function BucketCard({
    bucket,
    rankIndex,
    bucketIndex,
    rankNumber,
    resolvePageItems,
}: {
    bucket: BucketView
    rankIndex: number
    bucketIndex: number
    rankNumber?: number
    resolvePageItems?: (pageLabel: string) => PageItemSummary[]
}) {
    return (
        <View
            data-bandc-bucket={`${rankIndex}-${bucketIndex}`}
            dataTestId={`bandc-bucket-${rankIndex}-${bucketIndex}`}
            role='group'
            aria-label={`Bucket ${bucket.bucketId}, rank ${rankNumber}, ${bucket.cups.length} cups${
                bucket.selected ? ', selected (drawn) in this step' : ''
            }`}
            style={{
                position: 'relative',
                flex: '0 0 auto',
                width: 'auto',
                minWidth: 300,
                maxWidth: 900,
                border: bucket.selected ? '3px solid #111827' : '1px solid #cbd5e1',
                borderRadius: 12,
                padding: '12px 10px 10px',
                background: bucket.selected ? C.selFill : '#fff',
            }}
        >
            <CornerTag text='bucket' bg={bucket.selected ? C.selFill : '#fff'} />
            <Row gridGap='S100' alignItems='center' style={{ flexWrap: 'wrap' }}>
                <span
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: bucket.selected ? '#0b3a82' : '#111827',
                    }}
                >
                    {bucket.bucketId}
                </span>
                {bucket.selected && <Pill text='drawn' color={C.blue} />}
                <Spacer flex={1} />
                <Text fontSize='T75' color='neutral70'>
                    rank {bucket.rank} · maxFreq {bucket.maxFrequency} · {bucket.cups.length} cups
                </Text>
            </Row>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 16,
                    alignItems: 'flex-start',
                    marginTop: 8,
                }}
            >
                {bucket.cupViews.map((cup) => (
                    <CupChip
                        key={cup.cupId}
                        cup={cup}
                        bucketRank={bucket.rank}
                        resolvePageItems={resolvePageItems}
                    />
                ))}
            </div>
        </View>
    )
}

/* ------------------------------------------------------------------ diagram */
function Diagram({
    rows,
    resolvePageItems,
}: {
    rows: RankRow[]
    resolvePageItems?: (pageLabel: string) => PageItemSummary[]
}) {
    return (
        <Col gridGap='S300'>
            {rows.map((row, ri) => (
                <Col key={ri} gridGap='S100'>
                    <Row gridGap='S100' alignItems='center' style={{ flexWrap: 'wrap' }}>
                        <span
                            aria-hidden='true'
                            style={{
                                minWidth: 26,
                                height: 26,
                                borderRadius: 7,
                                background: C.blue,
                                color: '#fff',
                                fontSize: 15,
                                fontWeight: 800,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 6px',
                            }}
                        >
                            {row.rank}
                        </span>
                        <Text fontSize='T200' fontWeight='bold'>
                            Rank {row.rank}
                        </Text>
                        <Pill text={`${row.step.bucketSelectionRule} bucket`} color={C.green} />
                        <Pill text={`${row.step.cupSelectionRule} cups`} color='#9333ea' />
                    </Row>
                    <Text fontSize='T100' color='neutral70'>
                        {describeStep(row.step, row.totalBuckets)}
                    </Text>
                    <div
                        style={{
                            display: 'flex',
                            gap: 12,
                            overflowX: 'auto',
                            paddingTop: 12,
                            paddingBottom: 6,
                        }}
                    >
                        {row.buckets.map((b, bi) => (
                            <BucketCard
                                key={b.bucketId}
                                bucket={b}
                                rankIndex={ri}
                                bucketIndex={bi}
                                rankNumber={row.rank}
                                resolvePageItems={resolvePageItems}
                            />
                        ))}
                    </div>
                </Col>
            ))}
        </Col>
    )
}

/* ------------------------------------------------------------------- chips */

/* ------------------------------------------------------------------ summary */
const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <View
        style={{
            background: '#f8fafc',
            border: `1px solid ${C.line}`,
            borderRadius: 10,
            padding: '8px 12px',
            minWidth: 110,
            maxWidth: 220,
        }}
    >
        <Text fontSize='T75' color='neutral70'>
            {label}
        </Text>
        <Text fontSize='T300' fontWeight='bold'>
            {value}
        </Text>
    </View>
)

/* -------------------------------------------------------- simulation table */
interface SimResultRow {
    seq: number
    step: number
    rank?: number
    cupRank?: number
    bucketId: string
    cupId: string
    page: string
    pageVariable?: string
    tags: string[]
    formula?: string
}

/**
 * Flattens the drawn cups (across all steps) into one row per delivered page, in
 * delivery order. When the drawn page has an AudioEvaluation item, a CRA scoring
 * formula `CRA(<pageVariable>.<tag>, ...)` is built from the page pattern's tags.
 */
function buildSimResultRows(
    rows: RankRow[],
    resolvePageItems?: (pageLabel: string) => PageItemSummary[]
): SimResultRow[] {
    const out: SimResultRow[] = []
    rows.forEach((row, stepIdx) => {
        row.buckets.forEach((bucket) => {
            if (!bucket.selected) {
                return
            }
            bucket.cupViews.forEach((cup) => {
                if (!cup.drawn) {
                    return
                }
                const page = cup.selectedItemIds?.size
                    ? Array.from(cup.selectedItemIds)[0]
                    : cup.itemIds[0] ?? ''
                const hasAudioEvaluation =
                    !!resolvePageItems &&
                    resolvePageItems(page).some((it) => it.type === 'AudioEvaluation')
                const formula =
                    hasAudioEvaluation && cup.pageVariable && cup.tags.length > 0
                        ? `CRA(${cup.tags.map((t) => `${cup.pageVariable}.${t}`).join(', ')})`
                        : undefined
                out.push({
                    seq: cup.order ?? 0,
                    step: stepIdx + 1,
                    rank: bucket.rank,
                    cupRank: cup.rank,
                    bucketId: bucket.bucketId,
                    cupId: cup.cupId,
                    page,
                    pageVariable: cup.pageVariable,
                    tags: cup.tags,
                    formula,
                })
            })
        })
    })
    return out.sort((a, z) => a.seq - z.seq)
}

function SimulationResultsTable({
    rows,
    resolvePageItems,
}: {
    rows: RankRow[]
    resolvePageItems?: (pageLabel: string) => PageItemSummary[]
}) {
    const simRows = buildSimResultRows(rows, resolvePageItems)
    if (simRows.length === 0) {
        return null
    }

    // Only show the page-pattern columns when at least one drawn page has a
    // page variable or tags defined.
    const showPatterns = simRows.some((r) => !!r.pageVariable || r.tags.length > 0)

    const th: React.CSSProperties = {
        textAlign: 'left',
        padding: '6px 10px',
        fontSize: 11,
        fontWeight: 700,
        color: '#334155',
        borderBottom: `1px solid ${C.line}`,
        whiteSpace: 'nowrap',
    }
    const td: React.CSSProperties = {
        padding: '6px 10px',
        fontSize: 12,
        color: '#111827',
        borderBottom: `1px solid ${C.line}`,
        verticalAlign: 'top',
    }
    const mono: React.CSSProperties = {
        fontFamily: 'ui-monospace, Menlo, monospace',
        fontSize: 11,
    }

    return (
        <View
            dataTestId='bandc-sim-results'
            style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: 12 }}
        >
            <Text fontSize='T200' fontWeight='bold'>
                Simulation results
            </Text>
            <Text fontSize='T75' color='neutral70'>
                One row per delivered page, in delivery order.
            </Text>
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
                <table
                    style={{ borderCollapse: 'collapse', width: '100%' }}
                    aria-label='Simulation results, one row per delivered page in delivery order'
                >
                    <thead>
                        <tr>
                            <th style={th} scope='col' aria-label='Delivery order'>
                                #
                            </th>
                            <th style={th} scope='col'>
                                Step
                            </th>
                            <th style={th} scope='col'>
                                Bucket
                            </th>
                            <th style={th} scope='col'>
                                Cup
                            </th>
                            <th style={th} scope='col'>
                                Page
                            </th>
                            {showPatterns && (
                                <th style={th} scope='col'>
                                    Page variable
                                </th>
                            )}
                            {showPatterns && (
                                <th style={th} scope='col'>
                                    Tags
                                </th>
                            )}
                            {showPatterns && (
                                <th style={th} scope='col'>
                                    Scoring formula
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {simRows.map((r) => {
                            const color = bucketRankColor(r.rank)
                            return (
                                <tr key={r.seq}>
                                    <td style={{ ...td, borderLeft: `4px solid ${color}` }}>
                                        {r.seq}
                                    </td>
                                    <td style={td}>{r.step}</td>
                                    <td style={{ ...td, fontWeight: 700 }}>
                                        {r.bucketId} (rank {r.rank})
                                    </td>
                                    <td style={td}>
                                        <span
                                            aria-hidden='true'
                                            style={{
                                                display: 'inline-block',
                                                width: 8,
                                                height: 8,
                                                borderRadius: 2,
                                                background: color,
                                                marginRight: 6,
                                            }}
                                        />
                                        {r.cupId} (rank {r.cupRank})
                                    </td>
                                    <td style={td}>{r.page || '—'}</td>
                                    {showPatterns && <td style={td}>{r.pageVariable ?? '—'}</td>}
                                    {showPatterns && (
                                        <td style={td}>
                                            {r.tags.length > 0 ? r.tags.join(', ') : '—'}
                                        </td>
                                    )}
                                    {showPatterns && (
                                        <td
                                            style={{
                                                ...td,
                                                ...mono,
                                                color: r.formula ? '#0b3a82' : '#94a3b8',
                                            }}
                                        >
                                            {r.formula ?? '—'}
                                        </td>
                                    )}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </View>
    )
}

/* ======================================================= main component */
export const BucketsAndCupsVisualizer = ({
    group,
    resolvePageItems,
    generateSelectionPlan,
}: BucketsAndCupsVisualizerProps) => {
    const [selection, setSelection] = useState<number[]>(
        group.bucketAndCupSelectionStep.map(() => -1)
    )
    const [plan, setPlan] = useState<SelectionPlan | undefined>(undefined)
    const [planError, setPlanError] = useState<string | undefined>(undefined)

    const simulate = useCallback(() => {
        if (!generateSelectionPlan) {
            return
        }
        setPlanError(undefined)
        generateSelectionPlan()
            .then((p) => setPlan(p))
            .catch((e: unknown) =>
                setPlanError(e instanceof Error ? e.message : 'Simulation failed.')
            )
    }, [generateSelectionPlan])

    useEffect(() => {
        // Reset all per-module state when the module changes; do not auto-simulate
        // so nothing is shown as selected until the user runs a simulation.
        setPlan(undefined)
        setPlanError(undefined)
        setSelection(group.bucketAndCupSelectionStep.map(() => -1))
    }, [group])

    const rows = useMemo(() => buildRows(group, selection, plan), [group, selection, plan])

    const cupCount = group.buckets.reduce((n, b) => n + b.cups.length, 0)
    const pageSet = new Set<string>()
    group.buckets.forEach((b) => b.cups.forEach((c) => c.itemIds.forEach((id) => pageSet.add(id))))
    const pageCount = pageSet.size

    // Distinct item types present across the pool (for the color legend).
    const itemTypes = new Set<string>()
    if (resolvePageItems) {
        pageSet.forEach((label) => resolvePageItems(label).forEach((it) => itemTypes.add(it.type)))
    }
    const itemTypeList = Array.from(itemTypes)

    const shuffle = () => {
        if (generateSelectionPlan) {
            simulate()
            return
        }
        setSelection(
            group.bucketAndCupSelectionStep.map((step) => {
                const count = group.buckets.filter((b) => b.rank === step.bucketRank).length
                return Math.floor(Math.random() * Math.max(count, 1))
            })
        )
    }

    return (
        <Col gridGap='S300' dataTestId='buckets-and-cups-visualizer' style={{ fontSize: 12 }}>
            <Row gridGap='S200' alignItems='center' style={{ flexWrap: 'wrap' }}>
                <Stat label='ItemPool' value={group.poolVersionId || '—'} />
                <Stat label='Ranks' value={group.bucketAndCupSelectionStep.length} />
                <Stat label='Buckets' value={group.buckets.length} />
                <Stat label='Cups' value={cupCount} />
                <Stat label='Pages' value={pageCount} />
                <Spacer flex={1} />
                <Button
                    variant={ButtonVariant.Secondary}
                    onClick={shuffle}
                    dataTestId='bandc-shuffle'
                >
                    Simulate another draw
                </Button>
            </Row>

            <Text fontSize='T100' color='neutral70'>
                {generateSelectionPlan
                    ? 'Selection is driven by the backend simulate API — “Simulate another draw” re-runs it. Selected pages are highlighted.'
                    : '“Simulate another draw” picks a draw and highlights the selected pages. Buckets and cups scroll horizontally.'}
            </Text>

            {planError && (
                <Text fontSize='T100' color='#9a3412'>
                    Simulate API error: {planError}
                </Text>
            )}

            <SimulationResultsTable rows={rows} resolvePageItems={resolvePageItems} />

            <div
                style={{
                    border: `1px solid ${C.line}`,
                    borderRadius: 14,
                    padding: 12,
                }}
            >
                <Diagram rows={rows} resolvePageItems={resolvePageItems} />
            </div>

            {itemTypeList.length > 0 && (
                <Row gridGap='S300' style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    <Text fontSize='T100' color='neutral70' fontWeight='bold'>
                        Item types (shown on cups):
                    </Text>
                    {itemTypeList.map((type) => (
                        <Row key={type} gridGap='S100' alignItems='center'>
                            <View
                                aria-hidden='true'
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 3,
                                    background: colorForItemType(type),
                                    border: '1px solid #1f2937',
                                }}
                            />
                            <Text fontSize='T100' color='neutral70'>
                                {type}
                            </Text>
                        </Row>
                    ))}
                </Row>
            )}
        </Col>
    )
}

BucketsAndCupsVisualizer.displayName = 'BucketsAndCupsVisualizer'
